/**
 * LoginPage Component
 * Branded login screen for LifeScribe Admin Dashboard
 *
 * Features:
 * - Split-panel design (Brand showcase 55% | Login card 45%)
 * - Brand colors (Deep Teal, Warm Gold, Soft Cream)
 * - Premium animations (floating, shimmer, sparkle)
 * - Fully responsive (mobile hides left panel)
 * - Google OAuth authentication
 */

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { brandColors, brandTagline } from '../theme/brandColors';

// ============================================================================
// ANIMATIONS
// ============================================================================

const floatingAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmerAnimation = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

const sparkleAnimation = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
`;

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const LoginPageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const BrandPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 55%',
  background: `linear-gradient(135deg, ${brandColors.deepTeal} 0%, ${brandColors.deepTealDark} 100%)`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '48px',
  position: 'relative',
  overflow: 'hidden',

  // Decorative background pattern (subtle quill feathers)
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5C20 5 15 15 15 25C15 35 25 45 30 55C35 45 45 35 45 25C45 15 40 5 30 5Z' fill='%23FFFFFF' fill-opacity='0.03'/%3E%3C/svg%3E")`,
    backgroundSize: '60px 60px',
    pointerEvents: 'none',
  },

  [theme.breakpoints.down('md')]: {
    display: 'none', // Hide brand panel on mobile
  },
}));

const LoginPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 45%',
  backgroundColor: brandColors.softCream,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '48px',

  [theme.breakpoints.down('md')]: {
    flex: '1 1 100%',
    background: `linear-gradient(135deg, ${brandColors.deepTeal} 0%, ${brandColors.deepTealDark} 100%)`,
  },
}));

const LogoContainer = styled(Box)({
  animation: `${floatingAnimation} 6s ease-in-out infinite`,
  marginBottom: '32px',
  zIndex: 1,
});

const BrandLogo = styled('img')({
  height: '180px',
  width: 'auto',
  borderRadius: '24px',
  filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
});

const BrandTagline = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '28px',
  fontStyle: 'italic',
  color: brandColors.white,
  textAlign: 'center',
  marginTop: '24px',
  fontWeight: 400,
  maxWidth: '420px',
  lineHeight: 1.5,
  zIndex: 1,
  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
});

const GoldAccentLine = styled(Box)({
  width: '100px',
  height: '3px',
  marginTop: '32px',
  background: `linear-gradient(90deg, transparent, ${brandColors.warmGold}, transparent)`,
  backgroundSize: '200px 100%',
  animation: `${shimmerAnimation} 3s linear infinite`,
  borderRadius: '2px',
  zIndex: 1,
});

interface ParticleProps {
  delay: number;
  top: string;
  left: string;
}

const FloatingParticle = styled(Box)<ParticleProps>(({ delay, top, left }) => ({
  position: 'absolute',
  width: '6px',
  height: '6px',
  background: brandColors.warmGold,
  borderRadius: '50%',
  opacity: 0.3,
  animation: `${sparkleAnimation} 4s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  top,
  left,
}));

const BrandFooter = styled(Typography)({
  position: 'absolute',
  bottom: '48px',
  left: '48px',
  fontFamily: "'Source Sans Pro', sans-serif",
  fontSize: '14px',
  color: 'rgba(255,255,255,0.6)',
  zIndex: 1,
});

const LoginCard = styled(Box)(({ theme }) => ({
  backgroundColor: brandColors.white,
  borderRadius: '16px',
  padding: '48px',
  maxWidth: '420px',
  width: '100%',
  boxShadow: '0 20px 60px rgba(26, 95, 122, 0.15)',
  textAlign: 'center',

  [theme.breakpoints.down('sm')]: {
    padding: '32px',
    maxWidth: '100%',
  },
}));

const CardLogo = styled('img')(({ theme }) => ({
  height: '90px',
  width: 'auto',
  marginBottom: '28px',

  [theme.breakpoints.down('sm')]: {
    height: '70px',
  },
}));

const CardTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '32px',
  fontWeight: 600,
  color: brandColors.deepTeal,
  marginBottom: '8px',
});

const CardSubtitle = styled(Typography)({
  fontFamily: "'Source Sans Pro', sans-serif",
  fontSize: '16px',
  color: brandColors.slateGray,
  marginBottom: '32px',
});

const GoogleSignInButton = styled(Button)({
  backgroundColor: brandColors.deepTeal,
  color: brandColors.white,
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '16px',
  fontWeight: 600,
  height: '54px',
  borderRadius: '8px',
  textTransform: 'none',
  width: '100%',
  boxShadow: '0 4px 14px rgba(26, 95, 122, 0.25)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: brandColors.deepTealDark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(26, 95, 122, 0.35)',
  },

  '& .MuiButton-startIcon': {
    marginRight: '12px',
  },
});

const SecurityBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginTop: '24px',
  color: brandColors.slateGray,
  fontFamily: "'Source Sans Pro', sans-serif",
  fontSize: '13px',

  '& svg': {
    fontSize: '16px',
  },
});

const FooterLinks = styled(Box)({
  marginTop: '32px',
  paddingTop: '24px',
  borderTop: `1px solid ${brandColors.softCream}`,
  fontFamily: "'Source Sans Pro', sans-serif",
  fontSize: '12px',
  color: brandColors.slateGray,
  opacity: 0.8,

  '& a': {
    color: brandColors.slateGray,
    textDecoration: 'none',
    margin: '0 8px',

    '&:hover': {
      color: brandColors.deepTeal,
      textDecoration: 'underline',
    },
  },
});

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const BrandPanelContent: React.FC = () => {
  // Particle configurations (5 particles with different positions)
  const particles = [
    { delay: 0, top: '15%', left: '20%' },
    { delay: 0.8, top: '25%', left: '75%' },
    { delay: 1.6, top: '70%', left: '10%' },
    { delay: 2.4, top: '80%', left: '80%' },
    { delay: 3.2, top: '40%', left: '35%' },
  ];

  return (
    <BrandPanel>
      {/* Floating Particles */}
      {particles.map((particle, index) => (
        <FloatingParticle
          key={index}
          delay={particle.delay}
          top={particle.top}
          left={particle.left}
        />
      ))}

      {/* Centered Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 }}>
        {/* Logo with floating animation */}
        <LogoContainer>
          <BrandLogo src="/logo.png" alt="LifeScribe Logo" />
        </LogoContainer>

        {/* Brand Tagline */}
        <BrandTagline>{brandTagline}</BrandTagline>

        {/* Gold Accent Line with shimmer */}
        <GoldAccentLine />
      </Box>

      {/* Footer */}
      <BrandFooter>Preserving Legacies Since 2026. Patents Pending.</BrandFooter>
    </BrandPanel>
  );
};

const LoginPanelContent: React.FC = () => {
  const handleGoogleLogin = () => {
    // Redirect to Google OAuth - CRITICAL: Must match existing auth flow
    // "/" so the landing page is chosen per user by HomeRedirect (Ad Dashboard,
    // or the Overview for anyone without ad access).
    window.location.href = '/auth/login?provider=google&return_to=/';
  };

  return (
    <LoginPanel>
      <LoginCard>
        {/* Logo */}
        <CardLogo src="/logo.png" alt="LifeScribe Admin" />

        {/* Title */}
        <CardTitle>Admin Dashboard</CardTitle>

        {/* Subtitle */}
        <CardSubtitle>Sign in to manage the LifeScribe platform</CardSubtitle>

        {/* Google Sign-in Button */}
        <GoogleSignInButton
          variant="contained"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleLogin}
          disableElevation
        >
          Sign in with Google
        </GoogleSignInButton>

        {/* Security Badge */}
        <SecurityBadge>
          <LockOutlinedIcon />
          <span>Secured with Google Authentication</span>
        </SecurityBadge>

        {/* Footer Links */}
        <FooterLinks>
          © 2026 LifeScribe
          <span> • </span>
          <a href="/privacy">Privacy</a>
          <span> • </span>
          <a href="/terms">Terms</a>
        </FooterLinks>
      </LoginCard>
    </LoginPanel>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const LoginPage: React.FC = () => {
  return (
    <LoginPageContainer>
      <BrandPanelContent />
      <LoginPanelContent />
    </LoginPageContainer>
  );
};

export default LoginPage;
